import React, { useCallback, useMemo, useState } from "react";
import type { FormattedInvoicePayload } from "../../../formattedInvoice/formattedInvoiceData";
import { NativeReportsV31Preview, type NativePreviewResult } from "../../ReportsV3Preview";
import { createInternalFormattingExecution } from "../../nativeVisibility.mjs";
import { internalSessionOf } from "../internalConditionalSession";
import styles from "./ReportDesignerPage.module.css";

export default function InternalConditionalPreview({ payload }: { payload: FormattedInvoicePayload }) {
  const execution = useMemo(() => {
    if (!internalSessionOf(payload)) throw new Error("Internal Preview requires an admitted in-memory fixture.");
    return createInternalFormattingExecution(payload);
  }, [payload]);
  const [resolved, setResolved] = useState<{ payload: FormattedInvoicePayload; result: NativePreviewResult } | null>(null);
  const onResult = useCallback((result: NativePreviewResult) => setResolved({ payload, result }), [payload]);
  const result = resolved?.payload === payload ? resolved.result : null;
  return <>
    <p className={styles["designer-empty-note"]}>Controlled synthetic fixture Preview. No authenticated business data or backend execution is used.</p>
    <details className={styles["designer-empty-note"]}>
      <summary>Shared conditional diagnostics</summary>
      {!result ? <p>Resolving the shared Preview plan.</p> : "error" in result ? <p role="alert">{result.error.code}: {result.error.message}</p> : (
        <ul>{result.plan.visibility?.decisions.filter(decision => decision.ruleId || decision.matchedRuleIds?.length).map(decision => (
          <li data-testid={`conditional-decision-${decision.identity}`} key={decision.identity}>
            {decision.identity}: {decision.reportVisible ? "visible" : "hidden"}.
            {decision.ruleId && ` Visibility rule ${decision.ruleId}: ${decision.visibilityMatched === undefined ? "not evaluated (disabled)" : decision.visibilityMatched ? "matched" : "did not match"}.`}
            {` Scope: ${decision.scopes?.join(", ") || "document"}.`}
            {` Formatting applied: ${decision.matchedRuleIds?.join(", ") || "none"}.`}
          </li>
        ))}</ul>
      )}
    </details>
    <NativeReportsV31Preview payload={payload} visibilityExecution={execution} onResult={onResult} />
  </>;
}
